import * as React from "react"
import { gql } from "@apollo/client"
import { useForm } from "lib/hooks/useForm"
import * as yup from "yup"
import { Form } from "./Form"
import { Input } from "./Input"
import { Select } from "./Select"
import { Button, FormLabel, HStack, SimpleGrid, Stack, Text } from "@chakra-ui/react"
import { FormError } from "./FormError"
import { ButtonGroup } from "./ButtonGroup"
import { regionsIncNed } from "lib/static/filters"
// GraphQL hook will be generated after running codegen
import { useCreateDonationMutation } from "lib/graphql"

const _ = gql`
  mutation CreateDonation($data: CreateDonationInput!) {
    createDonation(data: $data) {
      donationId
      paymentUrl
    }
  }
`

const FormSchema = yup.object().shape({
  amount: yup
    .number()
    .nullable()
    .required("Bedrag is verplicht")
    .test("amount-validation", "Bedrag is verplicht", function (value) {
      if (value === null || value === undefined) return false
      if (value === -1) return true // Custom amount selected
      return value > 0 // Preset amount must be positive
    }),
  customAmount: yup.string().when("amount", {
    is: (val: number) => val === -1,
    then: (schema) =>
      schema
        .required("Vul het gewenste bedrag in")
        .test("valid-amount", "Ongeldig bedrag", function (value) {
          if (!value) return false
          const parsed = parseFloat(value.replace(/\./g, ""))
          return !isNaN(parsed) && parsed > 0
        }),
    otherwise: (schema) => schema.notRequired(),
  }),
  firstName: yup.string().required("Voornaam is verplicht"),
  lastName: yup.string().required("Achternaam is verplicht"),
  email: yup.string().email("Ongeldig e-mailadres").required("E-mail is verplicht"),
  region: yup.string().required("Regio is verplicht"),
})

const PRESET_AMOUNTS = [50, 100, 500, 1000]

export function DonateForm() {
  const [createDonation, { loading: createDonationLoading }] = useCreateDonationMutation()

  const defaultValues = {
    amount: null as number | null,
    customAmount: "",
    firstName: "",
    lastName: "",
    email: "",
    region: "",
  }

  const form = useForm({ defaultValues, schema: FormSchema })
  const selectedAmount = form.watch("amount")
  const isCustomAmount = selectedAmount === -1

  const handleAmountSelect = (amount: number) => {
    form.setValue("amount", amount, { shouldValidate: true })
    if (amount !== -1) {
      form.setValue("customAmount", "", { shouldValidate: false })
    }
  }

  const handleSubmit = async (data: yup.InferType<typeof FormSchema>) => {
    // Determine the final amount
    let finalAmount: number
    if (data.amount === -1) {
      // Custom amount
      const parsedAmount = parseFloat(data.customAmount?.replace(/\./g, "") || "0")
      if (isNaN(parsedAmount) || parsedAmount <= 0) {
        form.setError("customAmount", { message: "Ongeldig bedrag" })
        return
      }
      finalAmount = parsedAmount
    } else {
      finalAmount = data.amount
    }

    
    return form.handler(
      () =>
        createDonation({
          variables: { data: { amount: finalAmount, firstName: data.firstName, lastName: data.lastName, email: data.email, region: data.region } },
        }),
      {
        onSuccess: (result, toast) => {
          // Redirect to iDEAL payment page
          if (result?.createDonation?.paymentUrl) {
            window.location.href = result.createDonation.paymentUrl
          } else {
            toast({ status: "error", description: "Kon geen betalingslink genereren" })
          }
        },
        onAppError: (message, toast) => {
          toast({ status: "error", description: message })
        },
      },
    )
  }

  return (
    <Stack py={8} spacing={6} border="1px solid" borderColor="gray.200" borderRadius="md" p={8}>
      <Text fontSize="4xl" fontWeight="bold" textTransform="uppercase">Doneer aan Kunstbende</Text>
      <Form {...form} onSubmit={handleSubmit}>
        <Stack spacing={6}>
          {/* Amount Selection */}
          <Stack spacing={3}>
            <FormLabel mb={0} fontWeight="semibold">
              Welk bedrag wil je doneren? *
            </FormLabel>
            <HStack spacing={3} flexWrap="wrap">
              {PRESET_AMOUNTS.map((amount) => (
                <Button
                  key={amount}
                  type="button"
                  variant={selectedAmount === amount ? "solid" : "outline"}
                  colorScheme={selectedAmount === amount ? "yellow" : "gray"}
                  onClick={() => handleAmountSelect(amount)}
                  aria-label={`Doneer ${amount.toLocaleString("nl-NL")} aan Kunstbende`}
                >
                  €{amount.toLocaleString("nl-NL")}
                </Button>
              ))}
              <Button
                type="button"
                variant={isCustomAmount ? "solid" : "outline"}
                colorScheme={isCustomAmount ? "yellow" : "gray"}
                onClick={() => handleAmountSelect(-1)}
                aria-label="Doneer een ander bedrag aan Kunstbende"
              >
                Ander bedrag
              </Button>
            </HStack>
            {isCustomAmount && (
              <Input
                name="customAmount"
                label="Vul het gewenste bedrag in"
                placeholder="Bijvoorbeeld: 25"
                type="text"
                isRequired
                aria-label="Vul het gewenste bedrag in"
              />
            )}
            {form.formState.errors.amount && (
              <Text color="red.500" fontSize="sm">
                {form.formState.errors.amount.message}
              </Text>
            )}
          </Stack>

          {/* Name Fields */}
          <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={4}>
            <Input name="firstName" label="Voornaam" isRequired aria-label="Voornaam" />
            <Input name="lastName" label="Achternaam" isRequired aria-label="Achternaam" />
          </SimpleGrid>

          {/* Email */}
          <Input name="email" label="E-mail" type="email" isRequired />

          {/* Region Selection */}
          <Select
            name="region"
            label="Doneer aan een specifieke regio of Kunstbende Nederland?"
            options={regionsIncNed}
            placeholder="Kies een regio"
            isRequired
          />

          <FormError />

          <ButtonGroup>
            <Button
              type="submit"
              colorScheme="yellow"
              isLoading={form.formState.isSubmitting || createDonationLoading}
              textTransform="uppercase"
            >
              Doneer!
            </Button>
          </ButtonGroup>
        </Stack>
      </Form>
    </Stack>
  )
}

